import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";
import { eventApi } from "~/lib/api/event";
import { cn } from "~/lib/utils";

type CancelEventDialogProps = {
	eventId: string;
	eventTitle: string;
	onCancelled: () => void;
	children: React.ReactNode;
};

export const CancelEventDialog = ({
	eventId,
	eventTitle,
	onCancelled,
	children,
}: CancelEventDialogProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [reason, setReason] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleCancel = async () => {
		if (!reason.trim()) {
			toast.error("Please provide a reason for cancellation");
			return;
		}

		setIsLoading(true);
		try {
			await eventApi.cancelEvent(eventId, { reason });
			toast.success(
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-full bg-destructive/10">
						<AlertCircle className="w-5 h-5 text-destructive" />
					</div>
					<div className="flex flex-col gap-0.5">
						<p className="font-medium">Event Cancelled</p>
						<p className="text-sm text-muted-foreground">
							All guests will be notified
						</p>
					</div>
				</div>,
			);
			setIsOpen(false);
			onCancelled();
		} catch (error) {
			toast.error(
				<div className="flex flex-col gap-1">
					<p className="font-medium">Failed to cancel event</p>
					<p className="text-sm opacity-80">Please try again</p>
				</div>,
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="bg-black/90 backdrop-blur-xl border-white/10 text-white sm:max-w-md">
				<div className="flex items-start gap-4 pb-4">
					<div className="shrink-0 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
						<AlertCircle className="w-6 h-6 text-destructive" />
					</div>
					<div>
						<h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-br from-white via-white/90 to-white/80 bg-clip-text text-transparent">
							Cancel Event
						</h2>
						<p className="text-white/60 mt-2">
							Are you sure you want to cancel "{eventTitle}"? This action cannot
							be undone and all guests will be notified.
						</p>
					</div>
				</div>

				<div className="space-y-6 py-4">
					<div className="space-y-3">
						<label
							htmlFor="reason"
							className="text-base font-medium text-white/80 block"
						>
							Cancellation Reason
						</label>
						<Textarea
							id="reason"
							placeholder="Please provide a reason for cancelling the event..."
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							className={cn(
								"h-24 resize-none bg-black/20 border-white/10",
								"text-white placeholder:text-white/40",
								"focus:border-destructive/50 focus:ring-destructive/50",
							)}
						/>
					</div>

					<div className="flex justify-end gap-3">
						<Button
							variant="ghost"
							onClick={() => setIsOpen(false)}
							disabled={isLoading}
							className="text-white/60 hover:text-white hover:bg-white/5"
						>
							Never mind
						</Button>
						<Button
							variant="destructive"
							onClick={handleCancel}
							disabled={isLoading || !reason.trim()}
							className={cn(
								"bg-destructive hover:bg-destructive/90 text-destructive-foreground",
								"transition-all duration-300",
								"hover:scale-[1.02] active:scale-[0.98]",
								"shadow-lg shadow-destructive/25",
								"px-8",
							)}
						>
							{isLoading ? (
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
									<span>Cancelling...</span>
								</div>
							) : (
								"Cancel Event"
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
